// src/styles/tokens.js
//
// The Vault color tokens — single source of truth for all theme color.
// Spec: Cerebral wiki/webapp/the-vault/preferences/the-vault-colorway.md
// ADR:  Cerebral wiki/webapp/the-vault/decisions/color-tokens-css-custom-properties.md
//
// Two tiers:
//   palette  — primitives, named by appearance (--green-800). Raw values live
//              here and nowhere else.
//   semantic — roles, named by intent (--color-brand), referencing primitives.
//              Components consume these wherever a role exists.
// A future Dark theme re-declares semantic values under [data-theme="dark"];
// primitives and components stay untouched.
//
// JS consumers (charts) import { palette, semantic } instead of hardcoding.
// semantic values are var() references — valid in CSS and inline styles, but
// NOT in SVG presentation attributes (Recharts fill, icon color props):
// those consumers need palette literals.

import { createGlobalStyle } from 'styled-components';

export const palette = {
  'green-900': '#003326',
  'green-800': '#004434',
  'green-700': '#005C3C',
  'green-600': '#00704A',
  'green-500': '#008F5A',
  'green-400': '#00A86B',
  'orange-500': '#EA7D23',
  'orange-50': '#FEF6F1',
  'yellow-500': '#E6B800',
  'red-800': '#A01818',
  'red-700': '#B71C1C',
  'red-600': '#D32F2F',
  'red-50': '#FFDDDD',
  'black': '#0D0B07',
  'white': '#FFFFFF',
  'blue-600': '#0078D4',
  'cream-50': '#F8F6EF',
  'cream-100': '#F3EDE2',
  'cream-200': '#EEECE5',
  'cream-300': '#ECE8DB',
  'cream-400': '#E8E7DB',
  'cream-500': '#D9D8D4',
  'grey-700': '#333333',
  'grey-600': '#555555',
  'grey-500': '#666666',
  'grey-400': '#888888',
  'grey-300': '#CCCCCC',
  'grey-200': '#E0E0E0',
  'grey-100': '#EEEEEE',
  // Alpha ramps (suffix = opacity %). New transparencies snap to a step.
  'green-a08': 'rgba(0, 68, 52, 0.08)',
  'green-a15': 'rgba(0, 68, 52, 0.15)',
  'green-a30': 'rgba(0, 68, 52, 0.30)',
  'green-a45': 'rgba(0, 68, 52, 0.45)',
  'green-a60': 'rgba(0, 68, 52, 0.60)',
  'orange-a12': 'rgba(234, 125, 35, 0.12)',
  'orange-a30': 'rgba(234, 125, 35, 0.30)',
  'orange-a45': 'rgba(234, 125, 35, 0.45)',
  'orange-a60': 'rgba(234, 125, 35, 0.60)',
  'danger-a10': 'rgba(183, 28, 28, 0.10)',
  'danger-a15': 'rgba(183, 28, 28, 0.15)',
  'danger-a30': 'rgba(183, 28, 28, 0.30)',
  'ink-a45': 'rgba(13, 11, 7, 0.45)',
  'ink-a60': 'rgba(13, 11, 7, 0.60)',
  'ink-a70': 'rgba(13, 11, 7, 0.70)',
  'shadow-a05': 'rgba(0, 0, 0, 0.05)',
  'shadow-a08': 'rgba(0, 0, 0, 0.08)',
  'shadow-a15': 'rgba(0, 0, 0, 0.15)',
  'shadow-a25': 'rgba(0, 0, 0, 0.25)',
  'shadow-a35': 'rgba(0, 0, 0, 0.35)',
  'shadow-a45': 'rgba(0, 0, 0, 0.45)',
  'shadow-a55': 'rgba(0, 0, 0, 0.55)',
  'shadow-a65': 'rgba(0, 0, 0, 0.65)',
  'shadow-a80': 'rgba(0, 0, 0, 0.80)',
  'white-a15': 'rgba(255, 255, 255, 0.15)',
  'white-a20': 'rgba(255, 255, 255, 0.20)',
  'white-a25': 'rgba(255, 255, 255, 0.25)',
  'white-a40': 'rgba(255, 255, 255, 0.40)',
  'white-a55': 'rgba(255, 255, 255, 0.55)',
  'white-a66': 'rgba(255, 255, 255, 0.66)',
  'white-a80': 'rgba(255, 255, 255, 0.80)',
  'white-a90': 'rgba(255, 255, 255, 0.90)',
};

export const semantic = {
  'color-brand': 'var(--green-800)',
  'color-brand-hover': 'var(--green-900)',
  'color-accent': 'var(--orange-500)',
  'color-accent-soft': 'var(--orange-50)',
  'color-text': 'var(--black)',
  'color-text-secondary': 'var(--grey-600)',
  'color-text-muted': 'var(--ink-a60)',
  'color-text-on-brand': 'var(--white)',
  'color-text-on-dark': 'var(--cream-400)',
  'color-surface': 'var(--white)',
  'color-surface-glass': 'var(--white-a66)',
  'color-app-bg': 'var(--cream-400)',
  'color-border': 'var(--grey-300)',
  'color-divider': 'var(--grey-200)',
  'color-danger': 'var(--red-700)',
  'color-danger-hover': 'var(--red-800)',
  'color-danger-text': 'var(--red-600)',
  'color-danger-soft': 'var(--red-50)',
  'color-success': 'var(--green-400)',
  'color-success-hover': 'var(--green-500)',
  // Lifecycle status chips — shared by Customer Specs (utils/specStatus.js) and
  // Package Index (utils/pidStatus.js). Backgrounds carry white text except Pending
  // and Incoming, which use --color-text (white on orange/yellow fails WCAG AA).
  // Each util pairs a background with its legible foreground.
  'color-status-active': 'var(--green-700)',
  'color-status-incoming': 'var(--yellow-500)',
  'color-status-pending': 'var(--orange-500)',
  'color-status-draft': 'var(--grey-600)',
  'color-status-inactive': 'var(--red-700)',
  'color-focus-ring': 'var(--orange-a45)',
  'color-focus-ring-brand': 'var(--green-a45)',
  'color-overlay': 'var(--shadow-a45)',
  'color-link-hover': 'var(--orange-500)',
};

const toCssVars = (tokens) =>
  Object.entries(tokens)
    .map(([name, value]) => `--${name}: ${value};`)
    .join('\n    ');

const TokenStyles = createGlobalStyle`
  :root {
    ${toCssVars(palette)}
    ${toCssVars(semantic)}
  }
`;

export default TokenStyles;
